import { Link } from "react-router-dom";
import { assetUrl } from "../api/client";
import { useRecordingClips } from "../api/clips";
import type { RecordingDetail } from "../api/types";
import ClipStatusBadge from "./ClipStatusBadge";

// Side-column card on the recording detail page: this recording's clips + the entry point
// into clipping mode. Management (retry/delete) lives on the Clips page; here it's a
// glanceable list with per-clip download once ready.

interface Props {
  rec: RecordingDetail;
  clipMode: boolean;
  onStartClipping: () => void;
}

function fmt(ms: number): string {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export default function ClipsCard({ rec, clipMode, onStartClipping }: Props) {
  const clips = useRecordingClips(rec.id);
  const items = clips.data?.items ?? [];
  const clippable =
    (rec.status === "pending_review" || rec.status === "approved") &&
    Boolean(rec.video_url) &&
    (rec.duration_ms ?? 0) > 0;

  return (
    <section className="card">
      <div className="card-head">
        <h3>Clips</h3>
        {items.length > 0 && <span className="muted small">{items.length}</span>}
      </div>

      {items.length === 0 && (
        <p className="muted small">No clips yet. Mark activity windows to deliver them as clips.</p>
      )}
      {rec.clip_suggestions && rec.clip_suggestions.proposed > 0 && !clipMode && (
        <p className="sugg-hint">
          ✦ {rec.clip_suggestions.proposed} AI clip suggestion
          {rec.clip_suggestions.proposed === 1 ? "" : "s"} ({fmt(rec.clip_suggestions.proposed_ms)})
          waiting in the composer
        </p>
      )}

      {items.length > 0 && (
        <ul className="clip-list">
          {items.map((c) => (
            <li key={c.id} className="clip-item" title={c.error ?? undefined}>
              <div className="clip-item-main">
                <span className="mono small">
                  {fmt(c.snapped_start_ms ?? c.start_ms)}–{fmt(c.snapped_end_ms ?? c.end_ms)}
                  {c.suggestion_id != null && (
                    <span className="ai-tag" title="Accepted from an AI suggestion">
                      ✦ AI
                    </span>
                  )}
                </span>
                <span className="clip-item-task">{c.task_title ?? c.task_id}</span>
              </div>
              <div className="clip-item-side">
                <ClipStatusBadge status={c.status} />
                {c.download_url && (
                  <a className="btn small" href={assetUrl(c.download_url)}>
                    Download
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="action-buttons">
        <button
          className="btn primary"
          onClick={onStartClipping}
          disabled={!clippable || clipMode}
          title={
            clippable
              ? "Mark activity windows on the video to deliver them as clips"
              : "Clipping is available once the recording is pending review or approved"
          }
        >
          {clipMode ? "Clipping…" : "Create clips"}
        </button>
        {items.length > 0 && (
          <Link className="btn small ghost" to="/clips">
            All clips →
          </Link>
        )}
      </div>
    </section>
  );
}
